import { useEffect, useRef, useState } from "react";

const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
let loaderPromise = null;

function ready() { return !!window.google?.maps?.Map; }

function loadGMaps() {
  if (ready()) return Promise.resolve();
  if (loaderPromise) return loaderPromise;
  loaderPromise = new Promise((resolve, reject) => {
    const waitReady = () => {
      let tries = 0;
      const iv = setInterval(() => {
        if (ready()) { clearInterval(iv); resolve(); }
        else if (++tries > 120) { clearInterval(iv); reject(new Error("gmaps timeout")); }
      }, 50);
    };
    const existing = document.querySelector("script[data-gmaps]");
    if (existing) { waitReady(); return; }
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${KEY}`;
    s.async = true; s.defer = true;
    s.setAttribute("data-gmaps", "1");
    s.onload = waitReady;
    s.onerror = () => reject(new Error("gmaps load error"));
    document.head.appendChild(s);
  });
  return loaderPromise;
}

// Plots numbered dispatch stops + the live driver pin.
export function DispatchMap({ stops = [], driver = null, height = 360 }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const objs = useRef({ markers: [] });
  const fitRef = useRef("");
  const [err, setErr] = useState(!KEY);

  useEffect(() => {
    if (!KEY) { setErr(true); return; }
    let alive = true;
    loadGMaps().then(() => {
      if (!alive || !ref.current) return;
      try {
        if (!mapRef.current) {
          mapRef.current = new window.google.maps.Map(ref.current, {
            zoom: 10, center: { lat: 19.0760, lng: 72.8777 },
            mapTypeControl: false, streetViewControl: false, fullscreenControl: true,
          });
        }
        draw();
      } catch { setErr(true); }
    }).catch(() => setErr(true));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (mapRef.current && window.google?.maps) draw(); });

  function clear() {
    objs.current.markers.forEach((m) => m.setMap(null));
    objs.current = { markers: [] };
  }

  function draw() {
    const g = window.google.maps;
    const map = mapRef.current;
    clear();
    const bounds = new g.LatLngBounds();
    let count = 0;
    const located = stops.filter((s) => s.lat != null);
    located.forEach((s, i) => {
      const done = s.status === "done";
      const color = done ? "#2F6F4E" : s.stop_type === "pickup" ? "#8A6D1B" : "#C84B31";
      const m = new g.Marker({
        position: { lat: s.lat, lng: s.lng }, map,
        label: { text: String(i + 1), fontSize: "11px", fontWeight: "700", color: "#fff" },
        icon: { path: g.SymbolPath.CIRCLE, scale: 11, fillColor: color, fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2 },
        title: `${s.company || ""} — ${s.address || ""}`,
      });
      objs.current.markers.push(m);
      bounds.extend({ lat: s.lat, lng: s.lng });
      count += 1;
    });
    if (driver && driver.lat != null) {
      const dm = new g.Marker({
        position: { lat: driver.lat, lng: driver.lng }, map,
        label: { text: "EV", fontSize: "10px", fontWeight: "700", color: "#fff" },
        icon: { path: g.SymbolPath.CIRCLE, scale: 12, fillColor: "#1A73E8", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 3 },
        title: "Driver",
      });
      objs.current.markers.push(dm);
      bounds.extend({ lat: driver.lat, lng: driver.lng });
      count += 1;
    }
    const sig = located.map((s) => `${s.lat},${s.lng}`).join("|") + `|d:${driver?.lat != null}`;
    if (count && fitRef.current !== sig) {
      fitRef.current = sig;
      if (count === 1) { map.setCenter(bounds.getCenter()); map.setZoom(13); }
      else { map.fitBounds(bounds, 60); }
    }
  }

  if (err) return <div className="text-sm text-[#8A2526] border border-dashed border-[#E5E3DB] p-4 rounded" data-testid="dispatch-map-unavailable">Map unavailable — Google Maps key missing or invalid.</div>;
  return <div ref={ref} style={{ height, width: "100%" }} className="border border-[#E5E3DB] rounded" data-testid="dispatch-map" />;
}

export default DispatchMap;
